import React from 'react';
import { useTranslation } from 'react-i18next';
import { useServiceCenterSettings } from '../../../hooks/useServiceCenterSettings';

interface HowItWorksProps {
  onBookClick?: () => void;
}

export default function HowItWorks({ onBookClick }: HowItWorksProps) {
  const { t } = useTranslation();
  const { latestOrderTime, latestDeliveryTime, skiReadyTime, earliestReturnTime } = useServiceCenterSettings();

  const steps = [
    {
      id: 1,
      icon: 'ri-calendar-check-line',
      titleMobile: t('howItWorks.step1.titleMobile', { orderDeadline: latestOrderTime }),
      title: t('howItWorks.step1.title', { orderDeadline: latestOrderTime }),
      description: t('howItWorks.step1.description', { orderDeadline: latestOrderTime }),
      descriptionMobile: t('howItWorks.step1.descriptionMobile', { orderDeadline: latestOrderTime }),
      subtitle: '',
      image: null,
    },
    {
      id: 2,
      icon: 'ri-e-bike-2-line',
      titleMobile: t('howItWorks.step2.titleMobile'),
      title: t('howItWorks.step2.title'),
      description: t('howItWorks.step2.description'),
      descriptionMobile: t('howItWorks.step2.descriptionMobile'),
      subtitle: '',
      image: null,
    },
    {
      id: 3,
      icon: 'ri-tools-line',
      titleMobile: t('howItWorks.step3.titleMobile'),
      title: t('howItWorks.step3.title'),
      description: t('howItWorks.step3.description'),
      descriptionMobile: t('howItWorks.step3.descriptionMobile'),
      subtitle: 'Professioneller Service in unserer Partnerwerkstatt in Klosters',
      image: 'https://public.readdy.ai/ai/img_res/8010a53adee9ab6e8e5688931c86c9a0.jpg',
    },
    {
      id: 4,
      icon: 'ri-sun-line',
      titleMobile: t('howItWorks.step4.titleMobile', { returnTime: earliestReturnTime }),
      title: t('howItWorks.step4.title', { returnTime: earliestReturnTime }),
      description: t('howItWorks.step4.description', { returnTime: earliestReturnTime, skiReadyTime }),
      descriptionMobile: t('howItWorks.step4.descriptionMobile', { returnTime: earliestReturnTime }),
      subtitle: '',
      image: null,
    },
  ];

  return (
    <section id="ablauf" className="flex flex-col justify-center bg-[#F0EDE6] pt-12 pb-8 md:min-h-screen md:py-0">
      <div className="max-w-7xl mx-auto px-3 md:px-6 w-full">
        <div className="text-center mb-3 md:mb-10">
          {/* Mobile-Titel ohne "zu perfekt präparierten Ski" */}
          <h2 className="block md:hidden text-base sm:text-2xl font-bold text-gray-900">
            {t('howItWorks.titleMobile')}
          </h2>
          {/* Desktop-Titel vollständig */}
          <h2 className="hidden md:block text-5xl font-bold text-gray-900">
            {t('howItWorks.title')}
          </h2>
        </div>

        <div className="relative">
          {/* Connection Line Desktop (horizontal) */}
          <div
            className="hidden md:block absolute left-0 right-0 border-t-2 border-dashed border-[#2B8FBF]"
            style={{ top: '60px' }}
          />

          {/* Mobile Layout: vertical stack */}
          <div className="flex flex-col md:hidden relative">
            {steps.map((step, index) => (
              <div key={step.id} className="relative flex items-stretch gap-2">
                {/* Left column: icon + vertical dashed line */}
                <div className="flex flex-col items-center flex-shrink-0" style={{ width: '40px' }}>
                  {/* Icon */}
                  <div className="w-9 h-9 rounded-full border-[3px] border-[#2B8FBF] flex flex-col items-center justify-center relative z-10 shadow-md bg-white flex-shrink-0 gap-0">
                    <i className={`${step.icon} text-[12px] text-[#2B8FBF] leading-none`}></i>
                    <span className="text-[8px] font-bold text-[#2B8FBF] leading-none">{step.id}</span>
                  </div>
                  {/* Dashed line: fills the full height of the card next to it */}
                  {index < steps.length - 1 && (
                    <div
                      className="w-0 border-l-2 border-dashed border-[#2B8FBF]"
                      style={{ flex: '1 1 0%' }}
                    />
                  )}
                </div>

                {/* Right column: card */}
                <div className="flex-1 pb-2 min-w-0">
                  <div className="bg-white rounded-lg shadow-sm w-full overflow-hidden">
                    {step.image && (
                      <div className="w-full h-20 overflow-hidden relative">
                        <img
                          src={step.image}
                          alt="Ski-Service Partnerwerkstatt"
                          width="600"
                          height="80"
                          loading="lazy"
                          className="w-full h-full object-cover object-center"
                        />
                        <span className="absolute bottom-1 left-2 bg-black/50 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded tracking-wide uppercase">
                          Partnerwerkstatt in Klosters
                        </span>
                      </div>
                    )}
                    <div className="p-2">
                      <p className="text-xs font-bold text-gray-900 leading-snug mb-0.5 break-words">
                        {step.titleMobile}
                      </p>
                      {step.subtitle && (
                        <p className="text-[10px] font-semibold text-[#2B8FBF] leading-snug mb-0.5 break-words">
                          {step.subtitle}
                        </p>
                      )}
                      <p className="text-gray-600 text-[10px] leading-snug break-words">
                        {step.descriptionMobile}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Layout: horizontal grid */}
          <div className="hidden md:grid grid-cols-4 gap-8 relative">
            {steps.map((step) => (
              <div key={step.id} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="w-28 h-28 rounded-full border-4 border-[#2B8FBF] flex items-center justify-center mb-5 relative z-10 shadow-lg flex-shrink-0 bg-white">
                    <i className={`${step.icon} text-5xl text-[#2B8FBF]`}></i>
                  </div>
                  <div className="bg-white rounded-2xl shadow-md w-full overflow-hidden">
                    {step.image && (
                      <div className="w-full h-40 overflow-hidden relative">
                        <img
                          src={step.image}
                          alt="Ski-Service Partnerwerkstatt"
                          width="600"
                          height="160"
                          loading="lazy"
                          className="w-full h-full object-cover object-top"
                        />
                        <span className="absolute bottom-2 left-3 bg-black/50 text-white text-[10px] font-semibold px-2 py-0.5 rounded tracking-widest uppercase">
                          Partnerwerkstatt in Klosters
                        </span>
                      </div>
                    )}
                    <div className="p-5">
                      <div className="inline-block bg-[#2B8FBF] text-white px-1.5 py-0.5 rounded-full text-xs font-bold mb-2 whitespace-nowrap">
                        {t('howItWorks.stepLabel')} {step.id}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {step.title}
                      </h3>
                      {step.subtitle && (
                        <p className="text-sm font-semibold text-[#2B8FBF] mb-2">
                          {step.subtitle}
                        </p>
                      )}
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* JETZT BUCHEN Button */}
        <div className="flex justify-center mt-4 md:mt-10">
          <button
            onClick={onBookClick}
            className="bg-[#2B8FBF] hover:bg-[#2480ad] active:bg-[#1f6f96] text-white font-bold px-8 py-3 md:px-12 md:py-4 rounded-full text-sm md:text-base shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-calendar-check-line text-base md:text-lg"></i>
            {t('nav.jetztBuchen')}
          </button>
        </div>
      </div>
    </section>
  );
}